import { downloadFromUrl, showError, verifyStream, showIntegrityWarning } from './download-logic';
import { saveAs } from 'file-saver';

import './download.css'

/**
 * 检查是否支持 File System Access API
 */
function isFileSystemApiSupported(): boolean {
    return 'showSaveFilePicker' in window;
}

/**
 * 使用 File System API 进行流式下载（推荐），并附带完整性验证。
 *
 * 通过 tee() 分岔流，一路 pipe 到文件系统，另一路边读边增量计算哈希，
 * 两者真正并行执行，验证不需要额外内存。
 */
async function streamDownloadWithFileSystemApi(
    stream: ReadableStream,
    fileName: string,
    expectedHash: string,
    expectedSize: number,
    mime: MIMEType = '*/*',
): Promise<void> {
    try {
        const accept: Record<MIMEType, any> = {}
        accept[mime] = []

        // 1. 请求用户授权保存位置
        const fileHandle = await window.showSaveFilePicker({
            suggestedName: fileName,
            types: [{
                description: 'All files',
                accept,
            }],
        });

        // 2. 分岔流：一支写入文件，另一支用于增量验证
        const [fileBranch, verifyBranch] = stream.tee();

        // 3. 创建可写流并 pipe（不 await，让它在后台进行）
        const writableStream = await fileHandle.createWritable();
        const fileWritePromise = fileBranch.pipeTo(writableStream);

        // 4. 同时边读验证边增量计算哈希（与文件写入并行）
        const verifyResult = await verifyStream(verifyBranch, expectedHash, expectedSize);

        // 5. 等待文件写入完成
        await fileWritePromise;

        // 6. 处理验证结果
        if (!verifyResult.valid) {
            console.warn('Integrity check failed:', verifyResult);
            const keep = await showIntegrityWarning(verifyResult);
            if (!keep) {
                // 用户选择丢弃，尝试删除已保存的文件
                try {
                    const remove = (fileHandle as any).remove?.bind(fileHandle);
                    if (remove) {
                        await remove();
                    } else {
                        // 浏览器不支持删除操作，告知用户手动处理
                        showError(
                            'Your browser does not support removing the saved file automatically. ' +
                            'Please manually delete the file to ensure data integrity.'
                        );
                        return;
                    }
                } catch (removeErr) {
                    console.warn('Failed to remove file after integrity check failure:', removeErr);
                    showError(
                        'Failed to remove the saved file due to an unexpected error. ' +
                        'Please manually delete the file to ensure data integrity.'
                    );
                    return;
                }
                throw new Error('File discarded due to integrity check failure');
            }
        }

        console.log('File downloaded successfully using File System API!');
    } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
            throw new Error('User cancelled the save operation');
        }
        throw err;
    }
}

/**
 * 降级方案：使用 file-saver 库下载，并附带完整性验证。
 *
 * 降级方案本身就需要将数据加载到内存，因此先通过 tee() 分岔，
 * 一路收集数据用于保存，另一路传入 verifyStream 做并行验证。
 */
async function downloadWithFallback(
    stream: ReadableStream,
    fileName: string,
    expectedHash: string,
    expectedSize: number,
    mime?: string | undefined,
): Promise<void> {
    const [collectBranch, verifyBranch] = stream.tee();

    // 并行：收集数据 + 完整性验证
    const [collectPromise, verifyPromise] = await Promise.all([
        // 1. 收集所有数据
        (async () => {
            const reader = collectBranch.getReader();
            const chunks: Uint8Array[] = [];

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                chunks.push(value);
            }
            return chunks;
        })(),
        // 2. 完整性验证
        verifyStream(verifyBranch, expectedHash, expectedSize),
    ]);

    const chunks = collectPromise;
    const verifyResult = verifyPromise;

    // 3. 处理验证结果
    if (!verifyResult.valid) {
        console.warn('Integrity check failed:', verifyResult);
        const keep = await showIntegrityWarning(verifyResult);
        if (!keep) {
            throw new Error('Download cancelled due to integrity check failure');
        }
    }

    // 4. 转换为 Blob 并保存
    const blob = new Blob(chunks as BlobPart[], mime ? { type: mime } : undefined);
    saveAs(blob, fileName);

    console.log('File downloaded using file-saver fallback method');
}

/**
 * 显示完成消息
 */
function showComplete(): void {
    const container = document.querySelector('.container');
    if (container) {
        container.innerHTML = `
            <div style="text-align: center;">
                <div style="
                    width: 60px;
                    height: 60px;
                    margin: 0 auto 20px;
                    background-color: #4caf50;
                    border-radius: 50%;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                ">
                    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3">
                        <path d="M5 13l4 4L19 7"/>
                    </svg>
                </div>
                <h1 style="color: #333;">Download Complete!</h1>
                <p style="color: #666;">Your file has been saved successfully.</p>
            </div>
        `;
    }
}

/**
 * 主下载处理函数
 */
async function handleDownload(): Promise<void> {
    try {
        const result = await downloadFromUrl(window.location);

        if (result.success) {
            // 获取文件名（路径格式: /type/slug）
            const urlParts = window.location.pathname.split('/');
            const slug = urlParts[2];
            const fileName = result.meta.filename || slug || 'download';
            const expectedHash = result.meta.hash;
            const expectedSize = result.meta.size;

            // 尝试使用 File System API，不可用时降级
            if (isFileSystemApiSupported()) {
                try {
                    let mime: string | undefined = result.meta.mime
                    if (mime && mime.indexOf('/') < 0) {
                        mime = undefined
                    }
                    await streamDownloadWithFileSystemApi(
                        result.stream,
                        fileName,
                        expectedHash,
                        expectedSize,
                        mime as (MIMEType | undefined),
                    );
                    showComplete();
                    return;
                } catch (err) {
                    // 用户取消或 API 出错，降级到传统方式
                    console.warn('File System API failed, falling back:', err);
                }
            }

            // 降级方案：使用 file-saver
            await downloadWithFallback(
                result.stream,
                fileName,
                expectedHash,
                expectedSize,
                result.meta.mime,
            );
            showComplete();
        } else {
            console.error('Download failed:', result.error);
            showError(result.error);
        }
    } catch (error) {
        console.error('Unexpected error during download:', error);
        showError(String(error));
    }
}

// 安全启动：检查 DOM 是否已准备就绪
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', handleDownload);
} else {
    handleDownload();
}


